import type { PageLoad } from './$types';
import { browser } from '$app/environment';
import { AdvertisingPartnerType } from '$houdini';
import { POI_LIST_PAGE_DATASHEET_HEIGHT_OFFSET } from '$lib/constants/drawer';
import { createPartnerBoxmapsStore } from '$lib/services/store/partnerBoxmaps';
import { isHomePage } from '$lib/services/urls/routeIds';
import { routesPagename } from '$lib/services/urls/routesPagename';
import { sessionPoiListCanonicalLink } from '$lib/utils/sessionStorage';
import { enrichPoisStoreWithSelectedPoiData, getPoisStore } from '$routes/(map)/loadPois';
import { error } from '@sveltejs/kit';
import { derived, get, readable } from 'svelte/store';
import { getStaticMap } from './getStaticMap';
import { getPageSeoData } from './metas';

export const load = (async (event) => {
	const canonicalLinkOfCanonicalPage = event.params.address;
	const { lang, locale, searchParams, searchPOIStore, searchPOIFromAddressStore } = await event.parent();
	const { page, selectedPoi } = searchParams;

	if (page <= 0) {
		return error(404);
	}

	const poisStoreWithoutSelectedPoi = await getPoisStore({
		urlAddress: canonicalLinkOfCanonicalPage,
		event,
		searchPOIStore,
		searchPOIFromAddressStore,
		poiCategories: isHomePage(event.url) && !searchParams.poiCategories ? [] : searchParams.poiCategories,
	});

	const poisStore = await enrichPoisStoreWithSelectedPoiData(
		poisStoreWithoutSelectedPoi,
		selectedPoi,
		event,
	);

	const staticMapStore = !event.params.category
		? await getStaticMap(event, get(poisStore), canonicalLinkOfCanonicalPage)
		: readable(undefined);

	const seoDataStore = derived([staticMapStore, poisStore], ([$staticMapStore, $poisStore]) => {
		return getPageSeoData(event, {
			searchParams,
			locale,
			poisStore: $poisStore,
			staticMapStore: $staticMapStore,
			lang,
			poiList: get(poisStoreWithoutSelectedPoi)?.poisList ?? [],
		});
	});

	const isMapResultPageFromSearchParams = searchParams?.selectedAddress?.address && searchParams.selectedAddress.entityType;

	if (browser && sessionPoiListCanonicalLink) {
		if (searchParams.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE) {
			sessionPoiListCanonicalLink.remove?.();
		} else {
			const pageCanonicalLink = get(seoDataStore).canonicalLink;
			if (pageCanonicalLink) {
				sessionPoiListCanonicalLink.set?.(pageCanonicalLink);
			} else {
				sessionPoiListCanonicalLink.remove?.();
			}
		}
	}

	const isMapResultPage = event.params.address || isMapResultPageFromSearchParams;
	const mobileTopButtons = ['search'];

	if (isHomePage(event.url)) {
		mobileTopButtons.push('pois-hrt');
	}

	const pagename = isMapResultPage ? routesPagename.MAP_RESULT : routesPagename.HOME_PAGE;

	const boxmapsForPageStore = createPartnerBoxmapsStore([{
		type: AdvertisingPartnerType.BOOKING,
		context: 'map_result',
	}]);

	return {
		page,
		poisStore,
		mobileTopButtons,
		bottomsheetHeightOffset: POI_LIST_PAGE_DATASHEET_HEIGHT_OFFSET,
		pagename,
		seoDataStore,
		boxmapsForPageStore,
	};
}) satisfies PageLoad;
